// ======================================================================
// File: src/modules/ipe/dashboard/crm/components/RevenueTrendWidget.jsx
// Purpose:
// CRM Executive Revenue Analytics Widget
// ======================================================================

import React from "react";

export default function RevenueTrendWidget({
  revenueData = [],
  onViewDetails,
}) {
  const latestRevenue =
    revenueData?.length > 0
      ? revenueData[revenueData.length - 1].revenue
      : 0;

  const previousRevenue =
    revenueData?.length > 1
      ? revenueData[revenueData.length - 2].revenue
      : 0;

  const growth =
    previousRevenue > 0
      ? (
          ((latestRevenue - previousRevenue) /
            previousRevenue) *
          100
        ).toFixed(1)
      : 0;

  const forecastRevenue = (
    latestRevenue *
    (1 + growth / 100)
  ).toFixed(2);

  // =====================================================
  // SVG LINE CHART
  // =====================================================

  const maxRevenue =
    Math.max(
      ...revenueData.map((r) => r.revenue)
    ) || 1;

  const points = revenueData
    .map((item, index) => {
      const x =
        (index /
          (revenueData.length - 1 || 1)) *
        320;

      const y =
        100 -
        (item.revenue / maxRevenue) * 80;

      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "18px",
        boxShadow:
          "0 2px 10px rgba(0,0,0,.06)",
        border: "1px solid #e5e7eb",
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          marginBottom: "18px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 700,
          }}
        >
          Revenue Analytics
        </h3>

        <button
          onClick={onViewDetails}
          style={{
            border:
              "1px solid #d1d5db",
            background: "#fff",
            padding: "6px 12px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          View Details
        </button>
      </div>

      {/* =====================================================
          KPI SUMMARY
      ===================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "1fr 1fr 1fr",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        {/* Revenue */}

        <div
          style={{
            background: "#eff6ff",
            padding: "14px",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
            }}
          >
            Current Revenue
          </div>

          <div
            style={{
              marginTop: "4px",
              fontSize: "24px",
              fontWeight: 700,
              color: "#2563eb",
            }}
          >
            ₹ {latestRevenue} Cr
          </div>
        </div>

        {/* Growth */}

        <div
          style={{
            background: "#ecfdf5",
            padding: "14px",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
            }}
          >
            Growth Rate
          </div>

          <div
            style={{
              marginTop: "4px",
              fontSize: "24px",
              fontWeight: 700,
              color:
                growth >= 0
                  ? "#16a34a"
                  : "#dc2626",
            }}
          >
            {growth}%
          </div>
        </div>

        {/* Forecast */}

        <div
          style={{
            background: "#f5f3ff",
            padding: "14px",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
            }}
          >
            Forecast
          </div>

          <div
            style={{
              marginTop: "4px",
              fontSize: "24px",
              fontWeight: 700,
              color: "#7c3aed",
            }}
          >
            ₹ {forecastRevenue} Cr
          </div>
        </div>
      </div>

      {/* =====================================================
          HEALTH BADGE
      ===================================================== */}

      <div
        style={{
          marginBottom: "18px",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 14px",
          borderRadius: "999px",
          background:
            growth >= 0
              ? "#dcfce7"
              : "#fee2e2",
          color:
            growth >= 0
              ? "#166534"
              : "#991b1b",
          fontSize: "12px",
          fontWeight: 700,
        }}
      >
        {growth >= 0 ? "▲" : "▼"}
        Revenue Trend
        {growth >= 0
          ? " Growing"
          : " Declining"}
      </div>

      {/* =====================================================
          CHART
      ===================================================== */}

      <div
        style={{
          background: "#f8fafc",
          borderRadius: "12px",
          padding: "14px",
        }}
      >
        <svg
          viewBox="0 0 320 120"
          width="100%"
          height="180"
        >
          <polyline
            fill="none"
            stroke="#2563eb"
            strokeWidth="4"
            points={points}
          />

          {revenueData.map(
            (item, index) => {
              const x =
                (index /
                  (revenueData.length -
                    1 || 1)) *
                320;

              const y =
                100 -
                (item.revenue /
                  maxRevenue) *
                  80;

              return (
                <circle
                  key={index}
                  cx={x}
                  cy={y}
                  r="5"
                  fill="#2563eb"
                />
              );
            }
          )}
        </svg>

        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            marginTop: "-12px",
            fontSize: "12px",
            color: "#64748b",
          }}
        >
          {revenueData.map(
            (item) => (
              <span key={item.month}>
                {item.month}
              </span>
            )
          )}
        </div>
      </div>

      {/* =====================================================
          MONTHLY REVENUE
      ===================================================== */}

      <div
        style={{
          marginTop: "18px",
          borderTop:
            "1px solid #e5e7eb",
          paddingTop: "12px",
        }}
      >
        <div
          style={{
            fontWeight: 700,
            marginBottom: "10px",
            color: "#0f172a",
          }}
        >
          Monthly Revenue
        </div>

        {revenueData.map((item) => (
          <div
            key={item.month}
            style={{
              display: "flex",
              justifyContent:
                "space-between",
              padding: "6px 0",
              borderBottom:
                "1px solid #f1f5f9",
            }}
          >
            <span>{item.month}</span>

            <strong>
              ₹ {item.revenue} Cr
            </strong>
          </div>
        ))}
      </div>

      {/* =====================================================
          FOOTER
      ===================================================== */}

      <div
        style={{
          marginTop: "14px",
          display: "flex",
          justifyContent:
            "space-between",
          fontSize: "12px",
          color: "#64748b",
        }}
      >
        <span>
          Highest Revenue:
          <strong>
            {" "}
            ₹{maxRevenue} Cr
          </strong>
        </span>

        <span>
          Last Updated:
          <strong> Today</strong>
        </span>
      </div>
    </div>
  );
}